import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { FileText, Utensils, Apple, Lightbulb, Package, Award } from 'lucide-react'

interface ProductDetailsTabsProps {
  product: {
    description: string
    ingredients: string[]
    nutrition: {
      servingSize: string
      energy: string
      fat: string
      saturatedFat: string
      carbs: string
      sugars: string
      protein: string
      salt: string
    }
    usageSuggestions: string[]
    formats: { name: string; weight: string }[]
    certifications: string[]
  }
}

export function ProductDetailsTabs({ product }: ProductDetailsTabsProps) {
  const { t } = useTranslation('common')
  const [activeTab, setActiveTab] = useState('description')

  const ProductCertifications = () => {
    if (!product.certifications || product.certifications.length === 0) return null

    return (
      <div className="bg-[#FDF8F3] border border-[#F3E4D6] rounded-2xl p-6">
        <h3 className="font-serif text-lg font-semibold text-[#4E2118] mb-4">
          {t('productDetail.certifications.title')}
        </h3>
        <div className="flex flex-wrap gap-3">
          {product.certifications.map((cert) => (
            <div
              key={cert}
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-[#F3E4D6]"
            >
              {cert === 'senza-glutine' && (
                <>
                  <img
                    src="/images/senza-glutine.svg"
                    alt=""
                    className="w-5 h-5 object-contain"
                  />
                  <span className="text-sm font-medium text-[#4E2118]">
                    {t('productDetail.certifications.glutenFree')}
                  </span>
                </>
              )}
              {cert === 'dop' && (
                <>
                  <Award className="w-5 h-5 text-[#00441C]" />
                  <span className="text-sm font-medium text-[#4E2118]">DOP</span>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'description', label: t('productDetail.tabs.description'), icon: FileText },
    { id: 'ingredients', label: t('productDetail.tabs.ingredients'), icon: Utensils },
    { id: 'nutrition', label: t('productDetail.tabs.nutrition'), icon: Apple },
    { id: 'usage', label: t('productDetail.tabs.usage'), icon: Lightbulb },
    { id: 'formats', label: t('productDetail.tabs.formats'), icon: Package },
  ]

  const tabContent = {
    description: (
      <div className="space-y-6">
        <div className="prose prose-lg max-w-none">
          <p className="text-[#4E2118]/80 leading-relaxed text-lg">
            {product.description}
          </p>
          <p className="text-[#4E2118]/70 leading-relaxed">
            {t('productDetail.description.paragraph2')}
          </p>
        </div>

        <div className="bg-[#FDF8F3] rounded-2xl p-6 md:p-8">
          <h3 className="font-serif text-xl font-semibold text-[#4E2118] mb-4">
            {t('productDetail.quality.title')}
          </h3>
          <ul className="grid sm:grid-cols-2 gap-4">
            {[
              t('productDetail.quality.points.selectedMeats'),
              t('productDetail.quality.points.traditionalRecipe'),
              t('productDetail.quality.points.naturalIngredients'),
              t('productDetail.quality.points.qualityControl'),
            ].map((point, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-[#DF631E] rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-white text-sm font-bold">{index + 1}</span>
                </div>
                <span className="text-[#4E2118]/80">{point}</span>
              </li>
            ))}
          </ul>
        </div>

        <ProductCertifications />
      </div>
    ),

    ingredients: (
      <div className="space-y-6">
        <div className="bg-white border border-[#F3E4D6] rounded-2xl p-6 md:p-8">
          <h3 className="font-serif text-xl font-semibold text-[#4E2118] mb-6">
            {t('productDetail.ingredients.title')}
          </h3>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {product.ingredients.map((ingredient, index) => (
              <motion.div
                key={ingredient}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3 p-3 bg-[#FDF8F3] rounded-xl"
              >
                <div className="w-2 h-2 bg-[#DF631E] rounded-full" />
                <span className="text-[#4E2118] font-medium">{ingredient}</span>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="bg-[#00441C]/5 border border-[#00441C]/10 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-[#00441C] rounded-xl flex items-center justify-center">
              <img
                src="/images/senza-glutine.svg"
                alt=""
                className="w-8 h-8 object-contain"
              />
            </div>
            <div>
              <h4 className="font-semibold text-[#00441C] mb-1">
                {t('productDetail.ingredients.allergensTitle')}
              </h4>
              <p className="text-[#4E2118]/70 text-sm">
                {t('productDetail.ingredients.allergensText')}
              </p>
            </div>
          </div>
        </div>

        <ProductCertifications />
      </div>
    ),

    nutrition: (
      <div className="space-y-6">
        <div className="bg-white border border-[#F3E4D6] rounded-2xl overflow-hidden">
          <div className="bg-[#4E2118] text-white px-6 py-4">
            <h3 className="font-serif text-xl font-semibold">
              {t('productDetail.nutrition.title')}
            </h3>
            <p className="text-white/70 text-sm mt-1">
              {t('productDetail.nutrition.perServing', { size: product.nutrition.servingSize })}
            </p>
          </div>

          <div className="divide-y divide-[#F3E4D6]">
            {[
              { label: t('productDetail.nutrition.energy'), value: product.nutrition.energy },
              { label: t('productDetail.nutrition.fat'), value: product.nutrition.fat },
              { label: t('productDetail.nutrition.saturatedFat'), value: product.nutrition.saturatedFat, indent: true },
              { label: t('productDetail.nutrition.carbs'), value: product.nutrition.carbs },
              { label: t('productDetail.nutrition.sugars'), value: product.nutrition.sugars, indent: true },
              { label: t('productDetail.nutrition.protein'), value: product.nutrition.protein, highlight: true },
              { label: t('productDetail.nutrition.salt'), value: product.nutrition.salt },
            ].map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`flex justify-between items-center px-6 py-4 ${
                  item.indent ? 'pl-12 bg-[#FDF8F3]/50' : ''
                } ${item.highlight ? 'bg-[#DF631E]/5' : ''}`}
              >
                <span className={`${item.highlight ? 'font-semibold text-[#4E2118]' : 'text-[#4E2118]/80'}`}>
                  {item.label}
                </span>
                <span className={`font-medium ${item.highlight ? 'text-[#DF631E]' : 'text-[#4E2118]'}`}>
                  {item.value}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        <p className="text-sm text-[#4E2118]/60 italic">
          {t('productDetail.nutrition.disclaimer')}
        </p>

        <ProductCertifications />
      </div>
    ),

    usage: (
      <div className="space-y-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {product.usageSuggestions.map((suggestion, index) => (
            <motion.div
              key={suggestion}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group bg-white border border-[#F3E4D6] rounded-2xl p-6 hover:shadow-lg hover:border-[#DF631E]/30 transition-all duration-300"
            >
              <div className="w-12 h-12 bg-[#DF631E]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#DF631E] group-hover:scale-110 transition-all duration-300">
                <Lightbulb className="w-6 h-6 text-[#DF631E] group-hover:text-white transition-colors" />
              </div>
              <p className="text-[#4E2118] font-medium">{suggestion}</p>
            </motion.div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-[#DF631E]/10 to-[#4E2118]/5 rounded-2xl p-6 md:p-8">
          <h3 className="font-serif text-xl font-semibold text-[#4E2118] mb-4">
            {t('productDetail.usage.tipsTitle')}
          </h3>
          <ul className="space-y-3">
            {[
              t('productDetail.usage.tips.storage'),
              t('productDetail.usage.tips.serving'),
              t('productDetail.usage.tips.pairing'),
            ].map((tip, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-[#DF631E] rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-white text-xs font-bold">{index + 1}</span>
                </div>
                <span className="text-[#4E2118]/80">{tip}</span>
              </li>
            ))}
          </ul>
        </div>

        <ProductCertifications />
      </div>
    ),

    formats: (
      <div className="space-y-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {product.formats.map((format, index) => (
            <motion.div
              key={format.name}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="relative group bg-white border-2 border-[#F3E4D6] rounded-2xl p-6 hover:border-[#DF631E] hover:shadow-xl transition-all duration-300"
            >
              {/* Popular badge for first item */}
              {index === 0 && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#DF631E] text-white text-xs font-semibold rounded-full">
                  {t('productDetail.formats.popular')}
                </div>
              )}

              <div className="w-16 h-16 bg-[#FDF8F3] rounded-2xl flex items-center justify-center mb-4 group-hover:bg-[#DF631E]/10 transition-colors">
                <Package className="w-8 h-8 text-[#4E2118] group-hover:text-[#DF631E] transition-colors" />
              </div>

              <h4 className="font-serif text-lg font-semibold text-[#4E2118] mb-2">
                {format.name}
              </h4>
              <p className="text-[#DF631E] font-bold text-xl">{format.weight}</p>
            </motion.div>
          ))}
        </div>

        <div className="bg-[#4E2118] text-white rounded-2xl p-6 md:p-8">
          <h3 className="font-serif text-xl font-semibold mb-4">
            {t('productDetail.formats.customTitle')}
          </h3>
          <p className="text-white/80 mb-6">
            {t('productDetail.formats.customText')}
          </p>
          <a
            href="mailto:info@fioruccifood.it"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#DF631E] hover:bg-[#E87F45] text-white font-semibold rounded-full transition-colors"
          >
            {t('productDetail.formats.contactCta')}
          </a>
        </div>

        <ProductCertifications />
      </div>
    ),
  }

  return (
    <section id="product-details" className="py-16 md:py-24 bg-white relative">
      <div className="container-fiorucci">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 bg-[#DF631E]/10 text-[#DF631E] text-sm font-semibold uppercase tracking-wider rounded-full mb-4">
            {t('productDetail.sectionBadge')}
          </span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#4E2118]">
            {t('productDetail.sectionTitle')}
          </h2>
        </motion.div>

        {/* Tabs Navigation */}
        <div className="mb-8">
          <div className="flex flex-wrap justify-center gap-2 md:gap-4">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                    isActive
                      ? 'bg-[#DF631E] text-white shadow-lg shadow-[#DF631E]/25'
                      : 'bg-[#FDF8F3] text-[#4E2118]/70 hover:bg-[#F3E4D6] hover:text-[#4E2118]'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {tabContent[activeTab as keyof typeof tabContent]}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  )
}
